import { useTrialStatus } from "@/hooks/useTrialStatus";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Sparkles, Clock, Lock, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

export const TrialBanner = () => {
  const { t } = useTranslation();
  const { isInTrial, daysRemaining, trialExpired } = useTrialStatus();
  const { subscription } = useSubscription();
  const [visible, setVisible] = useState(false);

  const currentTier = subscription?.data?.currentTier || 'free';

  useEffect(() => {
    if (currentTier !== 'free') return;

    const alreadyShown = sessionStorage.getItem('trial_banner_shown');
    if (alreadyShown) return;

    const showReadOnly = !isInTrial && trialExpired;
    if (!isInTrial && !showReadOnly) return;

    setVisible(true);
    sessionStorage.setItem('trial_banner_shown', '1');

    const timer = setTimeout(() => setVisible(false), 5000);
    return () => clearTimeout(timer);
  }, [currentTier, isInTrial, trialExpired]);

  if (currentTier !== 'free') return null;

  const showReadOnlyBanner = !isInTrial && trialExpired && currentTier === 'free';
  if (!isInTrial && !showReadOnlyBanner) return null;
  if (!visible) return null;

  const dismiss = () => setVisible(false);

  if (showReadOnlyBanner) {
    return (
      <div className="fixed top-20 left-4 right-4 z-50 animate-in slide-in-from-top fade-in duration-300">
        <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-3 px-4 rounded-xl shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Lock className="h-5 w-5 shrink-0" />
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-sm font-semibold">
                  {t('trial.readOnlyBannerTitle')}
                </span>
                <span className="text-xs opacity-90">
                  {t('trial.readOnlyBannerMessage')}
                </span>
              </div>
            </div>
            <button onClick={dismiss} className="shrink-0 p-0.5 rounded-full hover:bg-white/20 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-2">
            <Link to="/subscription" onClick={dismiss}>
              <Button
                variant="secondary"
                size="sm"
                className="w-full bg-white hover:bg-white/90 text-red-600 font-semibold"
              >
                {t('trial.upgradeNow')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-20 left-4 right-4 z-50 animate-in slide-in-from-top fade-in duration-300">
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white py-3 px-4 rounded-xl shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Sparkles className="h-5 w-5 shrink-0 animate-pulse" />
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-sm font-semibold">
                {t('trial.bannerActive')}
              </span>
              <span className="text-xs opacity-90 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {t('trial.daysRemaining', { count: daysRemaining })}
              </span>
            </div>
          </div>
          <button onClick={dismiss} className="shrink-0 p-0.5 rounded-full hover:bg-white/20 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-2">
          <Link to="/subscription" onClick={dismiss}>
            <Button
              variant="secondary"
              size="sm"
              className="w-full bg-white/20 hover:bg-white/30 text-white border-white/40 font-medium backdrop-blur-sm"
            >
              {t('trial.upgradeNow')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
